import { Box, Button, Grid } from '@mui/material'
import Header from 'components/Header'
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRowSelectionModel
} from '@mui/x-data-grid'
import { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { DailyOrderType } from 'constant'
import useDailyOrder from 'hooks/useDailyOrder'
import { getDailyOrderStatusLabel } from 'utils'
import { SnackContext } from 'contexts/SnackContextProvider'
import dayjs from 'dayjs'

const DailyOrderList = () => {
  const navigate = useNavigate()
  const { company } = useParams()
  const [dailyOrders, setDailyOrders] = useState<any[]>([])
  const [gridLoading, setGridLoading] = useState(true)
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set()
  })
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10
  })
  const {
    fetchDailyOrders,
    handleDeleteDailyOrders,
    handleRequireToCancelOrders
  } = useDailyOrder()
  const { handleOpenSnackbar } = useContext(SnackContext)

  useEffect(() => {
    const fetchData = async () => {
      setGridLoading(true)
      const orders = await fetchDailyOrders(`${company}`, DailyOrderType.All)
      const mappedOrders = orders.map((order: any) => ({
        ...order,
        id: order._id,
        orderNumber: order.orderNumber,
        company: order.company,
        status: getDailyOrderStatusLabel(order.status),
        isSpecial: order.isSpecial || false
      }))
      setDailyOrders(mappedOrders)
      setGridLoading(false)
    }
    fetchData()
  }, [])
  const getSelectedOrder = (id: string) => {
    const order = dailyOrders.find(order => order.id === id)
    return order
  }

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 250 },
    { field: 'orderNumber', headerName: 'Order Number', width: 150 },
    { field: 'company', headerName: 'Company', width: 150 },
    { field: 'status', headerName: 'Status', width: 150 },
    {
      field: 'isSpecial',
      headerName: 'Special',
      width: 100,
      renderCell: params => (
        <span
          style={{
            color: params.value ? '#ff6b35' : '#666',
            fontWeight: params.value ? 'bold' : 'normal'
          }}
        >
          {params.value ? 'Yes' : 'No'}
        </span>
      )
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 250,
      valueFormatter: (params: string) => {
        if (!params) return ''
        return dayjs(params).format('YYYY-MM-DD HH:mm:ss')
      }
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 150,
      sortable: false,
      renderCell: params => (
        <Button
          variant='outlined'
          size='small'
          onClick={() => {
            navigate(`${params.row.id}`, {
              state: { order: getSelectedOrder(params.row.id) }
            })
          }}
        >
          View
        </Button>
      )
    }
  ]

  return (
    <Box>
      <Header />
      <Box sx={{ maxWidth: '80%', margin: '20px auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Grid container spacing={2}>
            <Grid>
              <Button
                variant='outlined'
                color='error'
                onClick={() => {
                  handleRequireToCancelOrders(
                    Array.from(selectionModel.ids) as string[],
                    company!,
                    setDailyOrders,
                    setSelectionModel,
                    handleOpenSnackbar
                  )
                }}
                disabled={selectionModel.ids.size === 0}
              >
                Require to Cancel
              </Button>
            </Grid>
            <Grid>
              <Button
                variant='contained'
                color='error'
                onClick={() => {
                  handleDeleteDailyOrders(
                    `${company}`,
                    Array.from(selectionModel.ids) as string[],
                    setDailyOrders,
                    selectionModel,
                    setSelectionModel,
                    handleOpenSnackbar
                  )
                }}
                disabled={selectionModel.ids.size === 0}
              >
                Delete
              </Button>
            </Grid>
          </Grid>
        </Box>
        <DataGrid
          rows={dailyOrders}
          columns={columns}
          loading={gridLoading}
          slotProps={{
            loadingOverlay: {
              variant: 'skeleton',
              noRowsVariant: 'skeleton'
            }
          }}
          checkboxSelection
          rowSelectionModel={selectionModel}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={newPaginationModel =>
            setPaginationModel(newPaginationModel)
          }
          onRowSelectionModelChange={newSelectionModel =>
            setSelectionModel(newSelectionModel)
          }
        />
      </Box>
    </Box>
  )
}

export default DailyOrderList
